import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CalendarIcon, Plus, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface PaymentSchedule {
  type: 'once_off' | 'split' | 'recurring';
  invoicing_date?: string;
  splits?: Array<{
    percentage: number;
    date?: string;
    trigger?: 'at_start' | 'at_delivery' | 'custom';
  }>;
  amount_per_interval?: number;
  start_date?: string;
  frequency?: 'monthly' | 'bimonthly' | 'quarterly' | 'semester';
  start_date_type?: 'project_start' | 'custom';
  payment_terms?: 'immediately' | '30_days' | '60_days' | '90_days' | '120_days';
}

interface PaymentScheduleFormProps {
  value: PaymentSchedule;
  onChange: (value: PaymentSchedule) => void;
  projectValue?: number;
  projectStartDate?: string;
  hideHeader?: boolean;
}

export function PaymentScheduleForm({ value, onChange, projectValue, projectStartDate, hideHeader = false }: PaymentScheduleFormProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();

  const handleTypeChange = (newType: PaymentSchedule['type']) => {
    const newSchedule: PaymentSchedule = { 
      type: newType,
      payment_terms: '60_days' // Default payment terms for all types
    };
    
    if (newType === 'split') {
      newSchedule.splits = [
        { percentage: 50, trigger: 'at_start' },
        { percentage: 50, trigger: 'at_delivery' }
      ];
    } else if (newType === 'recurring') {
      newSchedule.start_date_type = 'project_start';
    }
    
    onChange(newSchedule);
  };

  const getMonthsFromFrequency = (frequency: string) => {
    switch (frequency) {
      case 'monthly': return 12;
      case 'bimonthly': return 6;
      case 'quarterly': return 4;
      case 'semester': return 2;
      default: return 12;
    }
  };

  const handleFrequencyChange = (frequency: PaymentSchedule['frequency']) => {
    if (!frequency || !projectValue) return;
    
    const months = getMonthsFromFrequency(frequency);
    const amountPerInterval = Math.round(projectValue / months);
    
    onChange({
      ...value,
      frequency,
      amount_per_interval: amountPerInterval
    });
  };

  const addSplit = () => {
    const currentTotal = (value.splits || []).reduce((sum, split) => sum + split.percentage, 0);
    const remaining = 100 - currentTotal;
    
    onChange({
      ...value,
      splits: [
        ...(value.splits || []),
        { percentage: Math.max(0, remaining), trigger: 'custom' }
      ]
    });
  };

  const removeSplit = (index: number) => {
    onChange({
      ...value,
      splits: value.splits?.filter((_, i) => i !== index)
    });
  };

  const updateSplit = (index: number, field: keyof PaymentSchedule['splits'][0], fieldValue: any) => {
    const newSplits = [...(value.splits || [])];
    newSplits[index] = { ...newSplits[index], [field]: fieldValue };
    onChange({ ...value, splits: newSplits });
  };

  const totalPercentage = (value.splits || []).reduce((sum, split) => sum + split.percentage, 0);

  return (
    <Card>
      {!hideHeader && (
        <CardHeader>
          <CardTitle>Invoicing Schedule</CardTitle>
          <CardDescription>
            Configure how and when this project will be invoiced
          </CardDescription>
        </CardHeader>
      )}
      <CardContent className="space-y-4">
        <div className="w-1/2">
          <Label htmlFor="schedule-type">Schedule Type</Label>
          <Select
            value={value.type}
            onValueChange={handleTypeChange}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="once_off">Once-off Payment</SelectItem>
              <SelectItem value="split">Split Payment</SelectItem>
              <SelectItem value="recurring">Recurring Payment</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {value.type === 'once_off' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Invoicing Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !value.invoicing_date && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {value.invoicing_date ? format(new Date(value.invoicing_date), "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={value.invoicing_date ? new Date(value.invoicing_date) : undefined}
                      onSelect={(date) => onChange({ ...value, invoicing_date: date?.toISOString().split('T')[0] })}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                </PopoverContent>
              </Popover>
            </div>
            
            <div>
              <Label>Payment Terms</Label>
              <Select
                value={value.payment_terms || '60_days'}
                onValueChange={(terms: any) => onChange({ ...value, payment_terms: terms })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediately">Immediately</SelectItem>
                  <SelectItem value="30_days">30 days</SelectItem>
                  <SelectItem value="60_days">60 days</SelectItem>
                  <SelectItem value="90_days">90 days</SelectItem>
                  <SelectItem value="120_days">120 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {value.type === 'split' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <Label>Payment Splits</Label>
              <Button variant="outline" size="sm" onClick={addSplit}>
                <Plus className="h-4 w-4 mr-2" />
                Add Split
              </Button>
            </div>
            
            {value.splits?.map((split, index) => (
              <div key={index} className="flex gap-2 items-end p-3 border rounded-lg">
                <div className="flex-1">
                  <Label className="text-xs">Percentage</Label>
                  <Input
                    type="number"
                    value={split.percentage}
                    onChange={(e) => updateSplit(index, 'percentage', Number(e.target.value))}
                    placeholder="Percentage"
                    min="0"
                    max="100"
                  />
                </div>
                
                <div className="flex-1">
                  <Label className="text-xs">Trigger</Label>
                  <Select
                    value={split.trigger}
                    onValueChange={(val: any) => updateSplit(index, 'trigger', val)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="at_start">At Start</SelectItem>
                      <SelectItem value="at_delivery">At End</SelectItem>
                      <SelectItem value="custom">Custom Date</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {split.trigger === 'custom' && (
                  <div className="flex-1">
                    <Label className="text-xs">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {split.date ? format(new Date(split.date), "PP") : "Pick date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={split.date ? new Date(split.date) : undefined}
                            onSelect={(date) => updateSplit(index, 'date', date?.toISOString().split('T')[0])}
                            initialFocus
                            className={cn("p-3 pointer-events-auto")}
                          />
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeSplit(index)}
                  disabled={value.splits?.length === 1}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            
            <div className={`text-sm ${totalPercentage === 100 ? 'text-green-600' : 'text-red-600'}`}>
              Total: {totalPercentage}% {totalPercentage !== 100 && '(Must equal 100%)'}
            </div>
            
            <div className="w-1/2">
              <Label>Payment Terms</Label>
              <Select
                value={value.payment_terms || '60_days'}
                onValueChange={(terms: any) => onChange({ ...value, payment_terms: terms })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediately">Immediately</SelectItem>
                  <SelectItem value="30_days">30 days</SelectItem>
                  <SelectItem value="60_days">60 days</SelectItem>
                  <SelectItem value="90_days">90 days</SelectItem>
                  <SelectItem value="120_days">120 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {value.type === 'recurring' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Frequency</Label>
                <Select
                  value={value.frequency}
                  onValueChange={handleFrequencyChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="bimonthly">Bimonthly (2m)</SelectItem>
                    <SelectItem value="quarterly">Quarterly (3m)</SelectItem>
                    <SelectItem value="semester">Semester (6m)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {value.frequency && (
                <div>
                  <Label>Amount per Interval</Label>
                  <Input
                    type="number"
                    value={value.amount_per_interval || ''}
                    onChange={(e) => onChange({ ...value, amount_per_interval: Number(e.target.value) })}
                    placeholder="Amount"
                    readOnly={!!projectValue}
                  />
                  {projectValue && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Calculated: €{projectValue.toLocaleString()} ÷ {getMonthsFromFrequency(value.frequency)} months = €{Math.round(projectValue / getMonthsFromFrequency(value.frequency)).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Select
                  value={value.start_date_type || 'project_start'}
                  onValueChange={(type: any) => {
                    const updates: Partial<PaymentSchedule> = { start_date_type: type };
                    if (type === 'project_start' && projectStartDate) {
                      updates.start_date = projectStartDate;
                    } else if (type === 'custom') {
                      updates.start_date = undefined;
                    }
                    onChange({ ...value, ...updates });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="project_start">Start Date</SelectItem>
                    <SelectItem value="custom">Custom Date</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Payment Terms</Label>
                <Select
                  value={value.payment_terms || '60_days'}
                  onValueChange={(terms: any) => onChange({ ...value, payment_terms: terms })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediately">Immediately</SelectItem>
                    <SelectItem value="30_days">30 days</SelectItem>
                    <SelectItem value="60_days">60 days</SelectItem>
                    <SelectItem value="90_days">90 days</SelectItem>
                    <SelectItem value="120_days">120 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {value.start_date_type === 'custom' && (
              <div>
                <Label>Custom Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !value.start_date && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {value.start_date ? format(new Date(value.start_date), "PPP") : "Pick start date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={value.start_date ? new Date(value.start_date) : undefined}
                      onSelect={(date) => onChange({ ...value, start_date: date?.toISOString().split('T')[0] })}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}